import { computed, makeObservable, observable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getActiveTopEventsSports';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Common } from 'src/domains/common/Common';
import { EventsCollectionState } from 'src/domains/sportsbook/shared/Types';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { CompetitionId, EventId } from 'src_common/common/websocket2/id/WebsocketId';

export type PromotedSportSingleType = {
    competitions_list: Array<CompetitionType>;
    display_order: number;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
};

export type EventType = {
    display_order: number;
    id2: EventId;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
    smallEvent: EventListGroupEventItemType;
};

export type CompetitionType = {
    competition_id: CompetitionId;
    display_order: number;
    events_list: Array<EventType>;
    id: number;
    is_active: boolean;
    sport_id: string;
    universe: string;
};

type SportsFiltersType = {
    key: string;
    isActive: boolean;
};

type TopEventsSportType = Response200Type;

export type TopEventsSportSingleType = PromotedSportSingleType;

export class TopEventsState {
    private readonly activeTopEventsSportsResource: Resource<Response200Type | null>;

    @observable public openEvents: boolean = true;
    @observable public activeFilterKey: string = 'all';

    public static get = AutoWeakMap.create((common: Common) => new TopEventsState(common));

    private constructor(public readonly common: Common) {
        makeObservable(this);
        this.activeTopEventsSportsResource = new Resource(async (): Promise<Response200Type | null> => {
            const response = await this.common.trpcClient.client.cms.getActiveTopEventsSports.query();

            if (response.status === 200) {
                return response.body;
            }

            console.error(`Unexpected http response code=${response.status}`, response);
            return null;
        });
    }

    public setOpenEvents = (): void => {
        this.openEvents = !this.openEvents;
    };

    @computed private get topEventsSports(): TopEventsSportType {
        const response = this.activeTopEventsSportsResource.get();
        if (response.type === 'ready' && response.value !== null) {
            return response.value;
        }
        return [];
    }

    @computed private get topEventsSportsSportIds(): string[] {
        return this.topEventsSports.map((sport) => sport.sport_id);
    }

    @computed private get activeEvents(): Record<string, Array<EventListGroupEventItemType>> {
        const eventsForSport: Record<string, Array<EventListGroupEventItemType>> = {};
        for (const sport of this.topEventsSportsSportIds) {
            const events = EventsCollectionState.get(this.common).listOfSport(sport).eventItems;
            if (events !== null) {
                eventsForSport[sport] = events;
            }
        }
        return eventsForSport;
    }

    @computed public get possibleTopEventsSports(): PromotedSportSingleType[] {
        const possibleTopEventsSports: PromotedSportSingleType[] = [];
        for (const sport of this.topEventsSports) {
            if (sport.is_active === false) {
                continue;
            }
            const possibleCompetitions: CompetitionType[] = [];
            for (const competition of sport.competitions_list) {
                if (competition.is_active === false) {
                    continue;
                }
                const eventIdsForSport = this.activeEvents[sport.sport_id];
                const promoActiveEvents: EventType[] = [];
                if (eventIdsForSport === undefined) {
                    continue;
                }
                for (const promoEvent of competition.events_list) {
                    if (promoEvent.is_active === false) {
                        continue;
                    }
                    const promosmallEventModel = eventIdsForSport.find((event) => event.id === promoEvent.event_id);
                    if (promosmallEventModel !== undefined) {
                        promoActiveEvents.push({
                            display_order: promoEvent.display_order,
                            id2: promosmallEventModel.id2,
                            id: promoEvent.id,
                            is_active: promoEvent.is_active,
                            sport_id: promoEvent.sport_id,
                            universe: promoEvent.universe,
                            smallEvent: promosmallEventModel,
                        });
                    }
                }
                if (promoActiveEvents.length > 0) {
                    possibleCompetitions.push({
                        competition_id: this.common.models.id.getCompetitionId(competition.competition_id),
                        display_order: competition.display_order,
                        id: competition.id,
                        is_active: competition.is_active,
                        sport_id: competition.sport_id,
                        universe: competition.universe,
                        events_list: promoActiveEvents,
                    });
                }
            }
            if (possibleCompetitions.length > 0) {
                possibleTopEventsSports.push({
                    ...sport,
                    competitions_list: possibleCompetitions,
                });
            }
        }
        return possibleTopEventsSports;
    }

    @computed public get topEventsSportsToShow(): PromotedSportSingleType[] {
        return this.possibleTopEventsSports
            .filter((sport) => sport.sport_id === this.activeFilterKey || this.activeFilterKey === 'all')
            .sort((a, b) => (a.display_order > b.display_order ? 1 : -1));
    }

    @computed private get activeSportFilters(): Array<SportsFiltersType> {
        return this.possibleTopEventsSports.map((sport) => {
            return { key: sport.sport_id, isActive: true };
        });
    }

    @computed public get sportFiltersAll(): Array<SportsFiltersType> {
        return [{ key: 'all', isActive: true }, ...this.activeSportFilters];
    }

    @computed public get isTopEventsSportsDataLoading(): boolean {
        const topEventsSportsData = this.activeTopEventsSportsResource.get();
        return topEventsSportsData.type === 'loading';
    }

    public handleToggleIsSportFilterActive = (key: string): void => {
        this.activeFilterKey = key;
    };

    public isFilterActive = (key: string): boolean => {
        return this.activeFilterKey === key;
    };
}
